const CSRF_COOKIE = "dca_studio_csrf";
export interface ApiErrorBody {
  error?: string;
  message?: string;
  detail?: unknown;
}
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody | null,
    fallback: string,
  ) {
    super(body?.message ?? fallback);
    this.name = "ApiError";
  }
}
export function csrfToken(): string {
  const value = document.cookie
    .split("; ")
    .find((part) => part.startsWith(CSRF_COOKIE + "="))
    ?.slice(CSRF_COOKIE.length + 1);
  try {
    return value ? decodeURIComponent(value) : "";
  } catch {
    return "";
  }
}
let accountId: string | null = null;
export function setDraftAccount(id: number | null): void {
  accountId = id === null ? null : String(id);
}
export function assertDraftAccount(id: number): void {
  if (accountId !== String(id))
    throw new ApiError(
      401,
      null,
      "Учётная запись изменилась. Начните действие заново.",
    );
}
interface Options extends Omit<RequestInit, "headers"> {
  headers?: Record<string, string>;
  skipRefresh?: boolean;
  timeoutMs?: number;
}
let refresh: Promise<boolean> | null = null;
async function raw(
  path: string,
  options: Options,
  intendedAccount = accountId,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  };
  if (path.startsWith("/studio")) {
    if (intendedAccount !== accountId)
      throw new ApiError(
        401,
        null,
        "Учётная запись изменилась. Откройте задачу заново.",
      );
    if (intendedAccount !== null)
      headers["X-Draft-Account-ID"] = intendedAccount;
  }
  const method = (options.method ?? "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method))
    headers["X-CSRF-Token"] = csrfToken();
  if (
    options.body &&
    typeof options.body === "string" &&
    !headers["Content-Type"]
  )
    headers["Content-Type"] = "application/json";
  const timer = new AbortController();
  const deadline = setTimeout(() => timer.abort(), options.timeoutMs ?? 30_000);
  const abort = () => timer.abort();
  options.signal?.addEventListener("abort", abort, { once: true });
  try {
    return await fetch(path, {
      ...options,
      headers,
      credentials: "include",
      signal: timer.signal,
    });
  } catch {
    throw new ApiError(
      0,
      null,
      "Не удалось связаться с сервером. Сохранённые на устройстве данные остаются доступны.",
    );
  } finally {
    clearTimeout(deadline);
    options.signal?.removeEventListener("abort", abort);
  }
}
export async function apiRequest(
  path: string,
  options: Options = {},
): Promise<Response> {
  const intendedAccount = accountId;
  let response = await raw(path, options, intendedAccount);
  if (response.status === 401 && !options.skipRefresh) {
    refresh ??= raw("/auth/refresh", { method: "POST", skipRefresh: true })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        refresh = null;
      });
    if (await refresh) response = await raw(path, options, intendedAccount);
  }
  if (!response.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      /* status remains authoritative */
    }
    throw new ApiError(
      response.status,
      body,
      response.status === 409
        ? "Данные изменились на сервере. Загрузите актуальную версию; ваш черновик не перезаписан."
        : response.status === 403
          ? "Для этой учётной записи действие недоступно."
          : response.status === 401
            ? "Войдите снова, чтобы продолжить."
            : "Не удалось выполнить действие. Повторите попытку.",
    );
  }
  return response;
}
export async function apiFetch<T>(
  path: string,
  options: Options = {},
): Promise<T> {
  const response = await apiRequest(path, options);
  return response.status === 204
    ? (undefined as T)
    : ((await response.json()) as T);
}
export function errorMessage(error: unknown): string {
  return error instanceof Error && /[А-Яа-яЁё]/.test(error.message)
    ? error.message
    : "Не удалось выполнить действие. Повторите попытку.";
}
